//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Card, CardContent, CardHeader, Fade, Grid } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getTitleVersions } from '@/redux/slices/titleSlice';
import { addVersionKeys, setPrimaryVersionIds, setVersionSelections } from '@/redux/slices/orderPlacementSlice';
import { selectActiveTitleId } from '@/redux/selectors/titleSelectors';
import { selectPrimaryVersionIds, selectVersionSelections, selectStep } from '@/redux/selectors/orderPlacementSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import axios from '@/_utils/axios';
import { TabBadge } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import SectionHeading from '@/components/_common/SectionHeading';
import OPSelectionsEmpty from '@/components/OrderPlacement/OPSelectionsEmpty';
import OPOrderStepperButtons from '@/components/OrderPlacement/OPOrderStepperButtons';
import TitleSearchLoadingCard from '@/components/OrderPlacement/OPTitleSearch/TitleSearchLoadingCard';
import TitleBrowser from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowser';
import OPVersionSelections from '@/components/OrderPlacement/OPStep2VersionSelection/OPVersionSelections/OPVersionSelections';
import OPStep2Breadcrumbs from '@/components/OrderPlacement/OPStep2VersionSelection/OP2Step2Breadcrumbs';

const OPStep2VersionSelection = () => {
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const dispatch = useAppDispatch();
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const versionSelections = useAppSelector(selectVersionSelections);
  const primaryVersionIds = useAppSelector(selectPrimaryVersionIds);
  const step = useAppSelector(selectStep);

  useEffect(
    function handleFetchVersionsIfV1() {
      if (activeTitleId && !isTitleV2) {
        dispatch(getTitleVersions(activeTitleId));
      }
    },
    [activeTitleId, isTitleV2]
  );

  useEffect(() => {
    if (step === 1 && primaryVersionIds?.length > 0 && versionSelections.length === 0) {
      const versionPromises = primaryVersionIds.map((id: string | number) =>
        axios
          .get(`/inventory/titles/${id}/`)
          .then((res) => res.data?.data)
          .catch(() => null)
      );

      Promise.all(versionPromises).then((versionDataArray) => {
        const validVersions = versionDataArray.filter(Boolean);
        if (validVersions.length > 0) {
          dispatch(addVersionKeys(validVersions));
          validVersions.forEach((versionData: any) => {
            dispatch(setVersionSelections(versionData));
          });
          dispatch(setPrimaryVersionIds([]));
        }
      });
    }
  }, [step, primaryVersionIds, versionSelections.length, dispatch]);

  return (
    <>
      <Grid container spacing={5}>
        <TitleSearchLoadingCard>
          <OPStep2Breadcrumbs />
          <TitleBrowser />
        </TitleSearchLoadingCard>

        <Grid xs={12} md={5} item>
          <Fade in timeout={900}>
            <SelectedVersionsCard elevation={0}>
              <SelectedVersionsHeader
                title={
                  <StyledSectionHeading color="teal" icon={<PolarisIcon icon="version_based" width={22} />}>
                    Selected Versions
                    <TabBadge color="teal" count={versionSelections.length} />
                  </StyledSectionHeading>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                {!versionSelections.length ? <OPSelectionsEmpty text="Search for a title to begin adding versions" /> : <OPVersionSelections />}
              </CardContent>
            </SelectedVersionsCard>
          </Fade>
          <Grid container>
            <Grid item ml="auto" mt={5}>
              <OPOrderStepperButtons />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OPStep2VersionSelection;

const SelectedVersionsCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper
}));

const SelectedVersionsHeader = styled(CardHeader)(({ theme }) => ({
  color: theme.palette.background.paper
}));

const StyledSectionHeading = styled(SectionHeading)(({ theme }) => ({
  marginBottom: theme.spacing(1)
}));
